
/**
 * Schema.org Structured Data Utilities
 * 
 * This utility provides functions to generate structured data for different page types
 * following Schema.org specifications to enhance SEO.
 */

// Base types for Schema.org entities
interface SchemaBase {
  '@context': string;
  '@type': string;
  [key: string]: any; // Allow any additional properties
}

interface SchemaPerson {
  '@type': 'Person';
  name: string;
  url?: string;
  image?: string;
}

interface SchemaOrganization extends SchemaBase {
  '@type': 'Organization';
  name: string;
  url: string;
  logo: {
    '@type': 'ImageObject';
    url: string;
    width?: number;
    height?: number;
  };
  sameAs?: string[];
}

interface SchemaBreadcrumbItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item?: string;
}

interface SchemaFAQItem {
  '@type': 'Question';
  name: string;
  acceptedAnswer: {
    '@type': 'Answer';
    text: string;
  };
}

// Website schema
export const createWebsiteSchema = (): SchemaBase => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Gildy',
  url: 'https://www.gildy.it',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.gildy.it/search?q={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
});

// Organization schema
export const createOrganizationSchema = (): SchemaOrganization => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Gildy',
  url: 'https://www.gildy.it',
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.gildy.it/logo.png',
    width: 112,
    height: 112
  },
  sameAs: [
    'https://www.facebook.com/gildyit',
    'https://www.instagram.com/gildy_it',
    'https://twitter.com/gildy_it',
    'https://www.linkedin.com/company/gildy'
  ]
});

// Breadcrumbs schema
export const createBreadcrumbsSchema = (items: { name: string; url: string }[]): SchemaBase => {
  const itemListElement: SchemaBreadcrumbItem[] = items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://www.gildy.it${item.url}`
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement
  };
};

// Blog post schema
export interface BlogPostSchemaProps {
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  authorImage?: string;
  featuredImage?: string;
  slug: string;
  categories?: string[];
  tags?: string[];
  wordCount?: number;
  readingTime?: string;
}

export const createBlogPostSchema = (props: BlogPostSchemaProps): SchemaBase => {
  const {
    title,
    description,
    datePublished,
    dateModified,
    authorName,
    authorImage,
    featuredImage,
    slug,
    categories,
    tags,
    wordCount,
    readingTime
  } = props;

  const author: SchemaPerson = {
    '@type': 'Person',
    name: authorName
  };

  if (authorImage) {
    author.image = authorImage;
  }

  // Find related content URLs based on categories or tags
  const isPartOf = {
    '@type': 'Blog',
    name: 'Gildy Blog',
    url: 'https://www.gildy.it/blog'
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    image: featuredImage,
    datePublished,
    dateModified: dateModified || datePublished,
    author,
    publisher: {
      '@type': 'Organization',
      name: 'Gildy',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.gildy.it/logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.gildy.it/blog/${slug}`
    },
    ...(wordCount !== undefined && { wordCount }),
    ...(readingTime !== undefined && { timeRequired: `PT${readingTime.split(' ')[0]}M` }), // Convert "5 min" to "PT5M" format
    ...(categories && categories.length > 0 && { articleSection: categories.join(', ') }),
    ...(tags && tags.length > 0 && { keywords: tags.join(', ') }),
    isPartOf
  };
};

// Blog listing schema
export const createBlogListingSchema = (posts: any[]): SchemaBase => {
  const itemListElement = posts.map((post, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      url: `https://www.gildy.it/blog/${post.slug}`,
      image: post.featuredImage,
      datePublished: post.date,
      author: {
        '@type': 'Person',
        name: post.author?.name || 'Gildy'
      }
    }
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement
  };
};

// Local business schema (for dealers)
export interface LocalBusinessSchemaProps {
  name: string;
  description?: string;
  streetAddress: string;
  city: string;
  region?: string;
  postalCode?: string;
  country?: string;
  telephone?: string;
  url: string;
  image?: string;
  latitude?: number;
  longitude?: number;
  openingHours?: string;
  priceRange?: string;
  rating?: number;
  reviewCount?: number;
}

export const createLocalBusinessSchema = (props: LocalBusinessSchemaProps): SchemaBase => {
  const {
    name,
    description,
    streetAddress,
    city,
    region = 'RM',
    postalCode,
    country = 'IT',
    telephone,
    url,
    image,
    latitude,
    longitude,
    openingHours,
    priceRange = '€€',
    rating,
    reviewCount
  } = props;

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    description,
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality: city,
      addressRegion: region,
      postalCode,
      addressCountry: country
    },
    telephone,
    url,
    image,
    priceRange,
    ...(openingHours && { openingHours }),
    ...(latitude && longitude && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude,
        longitude
      }
    }),
    ...(rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating,
        bestRating: '5',
        worstRating: '1',
        ratingCount: reviewCount || 1
      }
    })
  };
};

// Dealers listing schema
export const createDealersListingSchema = (dealers: any[], defaultCity: string = ''): SchemaBase => {
  const itemListElement = dealers.map((dealer, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'LocalBusiness',
      name: dealer.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: dealer.address,
        addressLocality: dealer.city || defaultCity,
        addressRegion: 'RM',
        addressCountry: 'IT'
      },
      telephone: dealer.phone,
      url: `https://www.gildy.it/compro-oro/${dealer.city?.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-') || (defaultCity ? defaultCity.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-') : '')}/${dealer.id}`,
      ...(dealer.rating && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: dealer.rating,
          bestRating: '5',
          worstRating: '1'
        }
      })
    }
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement
  };
};

// FAQ schema
export const createFAQSchema = (questions: { question: string; answer: string }[]): SchemaBase => {
  const mainEntity: SchemaFAQItem[] = questions.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer
    }
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  };
};

// Product schema for gold/silver prices
export interface PriceProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: number;
  priceCurrency: string;
  url: string;
}

export const createPriceProductSchema = (props: PriceProductSchemaProps): SchemaBase => {
  const { name, description, image, price, priceCurrency, url } = props;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image,
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency,
      availability: 'https://schema.org/InStock',
      url
    }
  };
};

// Service schema for Gildy services
export interface ServiceSchemaProps {
  name: string;
  description: string;
  url: string;
  provider?: {
    name: string;
    url: string;
  };
  areaServed?: string;
}

export const createServiceSchema = (props: ServiceSchemaProps): SchemaBase => {
  const { name, description, url, provider, areaServed } = props;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url,
    ...(provider && {
      provider: {
        '@type': 'Organization',
        name: provider.name,
        url: provider.url
      }
    }),
    ...(areaServed && { areaServed })
  };
};

// Combine multiple schema objects into a single script
export const combineSchemas = (...schemas: SchemaBase[]): string => {
  if (schemas.length === 1) {
    return JSON.stringify(schemas[0]);
  }

  // For multiple schemas, use @graph
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': schemas
  });
};
